import React, { useContext, useEffect, useState } from 'react';
import parse, { DOMNode } from 'html-react-parser';
import { Box, Button, CircularProgress, Skeleton, Stack, Typography, Chip, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { translate as t } from 'hooks/useTranslations';
import manualHelper from '../../helpers/draftManualHelper';
import { ContentPage, TitleTextField } from '../styles';
import errorHelper from 'helpers/errorHelper';
import SectionEditor from '../SectionEditor';
import { usePolicyEditorContext } from './context';
import PolicyActions from './PolicyActions';
import { DraftManualDetailContext } from '../context';
import PolicyAutosave from './PolicyAutosave';
import ModifyPolicyStructure from './_components/ModifyPolicyStructure';
import PolicyCompare from './PolicyCompare';
import ToC from '../ToC';
import SectionTags from './SectionTags';
import AttachmentBox from '../AttachmentLibrary/_components/AttachmentBox';
import PendingUpdatesAlert from 'views/App/Portal/Policy/DraftManuals/Detail/PendingUpdatesAlert';
import DisabledPolicyAlert from 'views/App/Portal/Policy/DraftManuals/Detail/DisabledPolicyAlert';
import { useNavigate } from 'react-router-dom';
import contentConversionHelper from '../../helpers/contentConversionHelper';
//import PolicyHeadersEditable from './_components/PolicyHeaders';
import PolicyHeaders from '../_components/PolicyHeaders';
import { AppContext } from 'views/App/context';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import ManualActions from '../ManualActions';
import GuideSheetEditModal from '../GuideSheet/GuideSheetEditModal';
import { Description as DescriptionIcon, BallotOutlined as ReleaseNotesIcon } from '@mui/icons-material';
import useManagePolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePolicy';
import { useQueryClient } from 'react-query';
import ContentConversionHelper from '../../helpers/contentConversionHelper';
import { Header } from '../_components/rightDrawer/Headers';
import { Content } from '../_components/rightDrawer/Contents';
import useReleaseNotes from '../ReleaseNotes/useReleaseNotes';
import ReleaseNotesModal from '../ReleaseNotes/ReleaseNotesEditModal';
import { useTrackChanges } from 'components/ContentEditor/context/SharedTrackChangesContext';
// Import editor styles so they're available in display mode for tracked changes
import 'components/ContentEditorShared/themes/PhxEditorTheme.css';

type PolicyEditorProps = {
  id: string;
};

export type DrawerPanel = 'guideSheet' | 'releaseNotes' | null;
export type ModalAction = 'CREATE' | 'EDIT' | null;

const PolicyEditor: React.FC<PolicyEditorProps> = ({ id }: PolicyEditorProps) => {
  const [policyTitle, setPolicyTitle] = useState<string>('');
  const {
    manual,
    pendingUpdates,
    manualHasPermissions,
    isComparePolicyOpen,
    setIsComparePolicyOpen,
    showAccreditationTags,
    setShowAccreditationTags,
  } = useContext(DraftManualDetailContext);
  const { currentProfile, hasPermission, currentOrganizationId } = useContext(AppContext);
  const navigate = useNavigate();
  const { isFeatureEnabled } = useContext(AppContext);
  const { updateGuideSheet, approveGuideSheet } = useManagePolicy();
  const queryClient = useQueryClient();
  const { showChanges } = useTrackChanges();

  const {
    currentSection,
    enabledFilter,
    disabledFilter,
    filteredSections,
    filterSubsections,
    gettingLock,
    setGettingLock,
    handleAcquireLockClick,
    handleModifyClick,
    handleInsertSectionAfter,
    handleSectionChange,
    isOpenModifyPolicy,
    policyData,
    policyMode,
    policyNumber,
    policyQuery,
    setEnabledFilter,
    setDisabledFilter,
    setIsOpenModifyPolicy,
    updatePolicyData,
    acceptNewPolicy,
    rejectNewPolicy,
    acceptDisabledPolicy,
    rejectDisabledPolicy,
    isAcceptingNew,
    isRejectingNew,
    isAcceptingDisabled,
    isRejectingDisabled,
    accreditationTags,
    committedPolicyQuery,
  } = usePolicyEditorContext();

  const {
    currentReleaseNoteIndex,
    currentNote,
    handleNoteIndexChange,
    handleUpdateReleaseNotes,
    handleApproveReleaseNotes,
    amountOfReleaseNotes,
    notesStatus,
  } = useReleaseNotes({ data: policyData, invalidateQuery: 'draft_policy', type: 'POLICY' });

  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [activeDrawerPanel, setActiveDrawerPanel] = useState<DrawerPanel>(null);
  const [activeModalPane, setActiveModalPane] = useState<DrawerPanel>(null);
  const [modalAction, setModalAction] = useState<ModalAction>(null);

  const hasAwbSubscription =
    hasPermission('awbEssentials', currentOrganizationId || '') ||
    hasPermission('awbPremium', currentOrganizationId || '');

  const handleDrawerIconClick = (panel: DrawerPanel) => {
    setActiveDrawerPanel(panel);
    setRightDrawerOpen(true);
  };

  const handleSetRightDrawerOpen = (open: boolean) => {
    setRightDrawerOpen(open);
    if (!open) setActiveDrawerPanel(null);
  };

  const handleTitleChange = (title: string) => {
    updatePolicyData({ title });
    setPolicyTitle(title);
  };

  const handleRemoveAttachment = (item: any, attachmentId: string) => {
    const updatedAttachments = (item.attachments || []).filter((att: any) => att.id !== attachmentId);
    const updatedItem = { ...item, attachments: updatedAttachments };
    handleSectionChange(updatedItem);
  };

  const renderAddSectionButton = (onClick: () => void) => (
    <Button
      variant='text'
      size='small'
      startIcon={<AddIcon />}
      color='neutral'
      sx={{ whiteSpace: 'nowrap' }}
      onClick={onClick}
    >
      {t('draftManuals.policyEditor.addSectionButton')}
    </Button>
  );

  const areAllSectionsDisabled = () => {
    return filteredSections()?.every((section) => section.state === 'DISABLED');
  };

  useEffect(() => {
    if (policyData) {
      setPolicyTitle(policyData.title);
    }
    if (localStorage.getItem(`policy-lock_${id}`) !== null) {
      setIsComparePolicyOpen(true);
    }
  }, [policyData, setIsComparePolicyOpen]);

  const handlePendingUpdates = async () => {
    if (policyQuery.data && policyMode === 'EDITABLE') await handleModifyClick();
    if (policyQuery.data && policyMode === 'LOCKED') {
      try {
        await handleAcquireLockClick();
        navigate(`./policy:${policyData?.id}/review-updates`);
      } catch (err) {
        setGettingLock(false);
      }
    } else {
      navigate(`./policy:${policyData?.id}/review-updates`);
    }
  };

  const handleOpenModals = (panel: DrawerPanel, action?: ModalAction) => {
    setActiveModalPane(panel);
    setModalAction(action ?? null);
  };

  const handleCloseModals = () => {
    setActiveModalPane(null);
    setModalAction(null);
  };

  const handleGuideSheetSave = async ({
    title,
    status,
    content,
  }: {
    title: string;
    status: 'DRAFT' | 'APPROVED';
    content: string;
  }) => {
    if (manual && policyData) {
      return new Promise<void>((resolve, reject) => {
        updateGuideSheet(
          manual.id,
          policyData.id,
          {
            title,
            contentEditable: ContentConversionHelper.contentForEditor(content),
            content: ContentConversionHelper.contentForRender(content),
          },
          () => {
            queryClient.invalidateQueries(['draft_policy', manual.id, policyData.id]);
            resolve();
          },
          (error) => {
            reject(error);
          }
        );
      });
    }
  };

  const handleApproveGuideSheet = async () => {
    if (manual && policyQuery.data) {
      return new Promise<void>((resolve, reject) => {
        approveGuideSheet(
          manual.id,
          policyQuery.data.id,
          () => {
            queryClient.invalidateQueries(['draft_policy', manual.id, policyQuery.data.id]);
            resolve();
          },
          (err) => {
            reject(err);
          }
        );
      });
    }
  };

  return (
    <>
      <TakeoverLayout
        pageTitle={manual ? parse(manual.name ?? '') : ''}
        backUrl='/admin/policy/draft-manuals'
        leftDrawer={<ToC key={`policy:${id}`} />}
        menubarLeft={
          <>
            {manual && (
              <ManualActions
                manual={manual}
                manualHasPermissions={manualHasPermissions}
                currentUserId={currentProfile?.user?.id}
                showContentFilters={!!(policyData && !isOpenModifyPolicy)}
                enabledFilter={enabledFilter}
                disabledFilter={disabledFilter}
                onEnabledFilterChange={setEnabledFilter}
                onDisabledFilterChange={setDisabledFilter}
                showComparePolicy={!!(policyData && policyMode === 'EDITING' && !isOpenModifyPolicy)}
                isComparePolicyOpen={isComparePolicyOpen}
                onComparePolicyChange={setIsComparePolicyOpen}
                canComparePolicy={
                  !!(
                    committedPolicyQuery.data && JSON.stringify(committedPolicyQuery.data) != JSON.stringify(policyData)
                  )
                }
                currentPolicyData={policyData}
                isOpenModifyPolicy={isOpenModifyPolicy}
                setIsOpenModifyPolicy={setIsOpenModifyPolicy}
                policyMode={policyMode}
                showAccreditationTags={showAccreditationTags}
                setShowAccreditationTags={setShowAccreditationTags}
              />
            )}
          </>
        }
        contentHeaderLeft={
          <Stack
            direction='row'
            spacing={4}
            sx={{ flexGrow: 1 }}
          >
            {policyData && policyMode === 'EDITING' && !isOpenModifyPolicy ? <PolicyAutosave /> : <Box></Box>}
          </Stack>
        }
        contentHeaderRight={
          <>
            {(gettingLock || isAcceptingNew || isRejectingNew) && <CircularProgress />}
            <Stack
              direction='row'
              spacing={4}
              sx={{ ml: 2 }}
            >
              <PolicyActions />
            </Stack>
          </>
        }
        rightDrawer={{
          verticalActions: [
            {
              key: 'guideSheet',
              icon: <DescriptionIcon />,
              label: t('draftManuals.guideSheet.title'),
              onClick: () => handleDrawerIconClick('guideSheet'),
              active: activeDrawerPanel === 'guideSheet',
            },
            {
              key: 'releaseNotes',
              icon: <ReleaseNotesIcon />,
              label: t('draftManuals.releaseNotes.title'),
              onClick: () => handleDrawerIconClick('releaseNotes'),
              active: activeDrawerPanel === 'releaseNotes',
            },
          ],
          header: (
            <Header
              activePaneId={activeDrawerPanel ?? ''}
              handleOpenModals={handleOpenModals}
              releaseNotes={{
                amountOfReleaseNotes,
                showControls: policyMode === 'EDITING' && manual?.manualLevel !== 'AGENCY',
              }}
              guideSheet={{ showControls: policyMode === 'EDITING' && manual?.manualLevel !== 'AGENCY' }}
            />
          ),
          content: (
            <Content
              activePaneId={activeDrawerPanel ?? ''}
              mode={policyMode}
              isLoading={policyQuery.isLoading || policyQuery.isFetching}
              guideSheets={{
                manualLevel: manual.manualLevel,
                guideSheet: policyData?.guideSheet,
                onApproveGuideSheet: handleApproveGuideSheet,
              }}
              releaseNotes={{
                onNoteIndexChange: handleNoteIndexChange,
                currentNoteIndex: currentReleaseNoteIndex,
                handleApproveReleaseNotes,
                releaseNotes: policyData?.releaseNotes,
              }}
            />
          ),
          wide: false,
        }}
        {...{
          rightDrawerOpen,
          setRightDrawerOpen: handleSetRightDrawerOpen,
        }}
      >
        <Stack
          spacing={4}
          sx={{ mx: 'auto', maxWidth: 900, width: '100%', px: 2 }}
        >
          {policyQuery.data?.isNew && policyQuery.data?.state === 'DISABLED' && (
            <PendingUpdatesAlert
              info={t('draftManuals.policy.newPolicy.prompt')}
              showAcceptReject={true}
              onAccept={acceptNewPolicy}
              onReject={rejectNewPolicy}
              containerClass={{ mx: 8, mb: 4 }}
              isLoading={isAcceptingNew || isRejectingNew}
            />
          )}
          {pendingUpdates &&
            pendingUpdates?.policies[id] &&
            pendingUpdates?.policies[id]?.count &&
            !pendingUpdates?.policies[id]?.isNewPolicyOnly &&
            policyMode !== 'EDITING' && (
              <>
                {pendingUpdates?.policies[id]?.policyDisabled ? (
                  <DisabledPolicyAlert
                    policyTitle={pendingUpdates?.policies[id]?.currentTitle || policyData?.title || ''}
                    policyNumber={pendingUpdates?.policies[id]?.policyNumber || ''}
                    onAccept={acceptDisabledPolicy}
                    onReject={rejectDisabledPolicy}
                    containerClass={{ mx: 8, mb: 4 }}
                    isLoading={isAcceptingDisabled || isRejectingDisabled}
                  />
                ) : (
                  <PendingUpdatesAlert
                    info={t('draftManuals.pendingUpdates.alert', '', {
                      count: pendingUpdates?.policies[id].count.toString(),
                    })}
                    actionText={t('draftManuals.pendingUpdates.actionText')}
                    onClick={handlePendingUpdates}
                    containerClass={{ mx: 8, mb: 4 }}
                    isLoading={gettingLock}
                    disable={policyMode === 'DISABLED'}
                  />
                )}
              </>
            )}
          {isFeatureEnabled('policy-headers')
            && manual?.headerConfiguration
            && manual.headerConfiguration.state === 'ENABLED' && (
              <PolicyHeaders
                manualHeaders={manual.headerConfiguration}
                manualId={manual.id}
                readOnly={true}
                translationPrefix="stagedManuals"
              />
            )}
          <ContentPage className={`phx-policy ${showChanges ? 'show-changes' : 'hide-changes'}`}>
            {policyQuery.isLoading || policyQuery.isError ? (
              <Box sx={{ minHeight: 0 }}>
                {policyQuery?.isError && errorHelper.renderErrors(policyQuery)}
                {policyQuery && (policyQuery.isLoading || policyQuery.isFetching) && (
                  <Stack spacing={2}>
                    <Skeleton
                      variant='rectangular'
                      height={28}
                      width='50%'
                    />
                    <Stack>
                      <Skeleton variant='text' />
                      <Skeleton variant='text' />
                      <Skeleton variant='text' />
                    </Stack>
                  </Stack>
                )}
              </Box>
            ) : (
              <Stack spacing={4}>
                {policyQuery.data && policyData ? (
                  <>
                    <Stack>
                      <Stack
                        direction='row'
                        spacing={3}
                        sx={{ alignItems: 'center' }}
                      >
                        <Typography variant='overline'>
                          {t('draftManuals.policyEditor.title')} {manualHelper.getNumberManualNode(policyData, manual)}
                        </Typography>
                        {showAccreditationTags &&
                          hasAwbSubscription &&
                          accreditationTags[policyData.id]?.length > 0 && (
                            <>
                              {accreditationTags[policyData.id].slice(0, 2).map((tag: string, index: number) => (
                                <Chip
                                  key={`policy_accreditation_tag_${index}`}
                                  label={tag}
                                  variant='filled'
                                  color='info'
                                  size='small'
                                />
                              ))}
                              {accreditationTags[policyData.id].length > 2 && (
                                <Tooltip
                                  title={
                                    <Box>
                                      {accreditationTags[policyData.id].slice(2).map((tag: string, index: number) => (
                                        <Box key={`policy_tag_tooltip_${index}`}>{tag}</Box>
                                      ))}
                                    </Box>
                                  }
                                  placement='top'
                                >
                                  <Chip
                                    label={`+${accreditationTags[policyData.id].length - 2}`}
                                    variant='filled'
                                    color='info'
                                    size='small'
                                  />
                                </Tooltip>
                              )}
                            </>
                          )}
                      </Stack>
                      {policyData && policyMode === 'EDITING' && !isOpenModifyPolicy ? (
                        <TitleTextField
                          id={`title_${policyData.id}`}
                          key={`title_${policyData.id}`}
                          label={false}
                          variant='outlined'
                          fullWidth
                          value={
                            policyData.title
                              ?.replace(/<[^>]*>/g, '')
                              ?.replace(/&amp;/g, '&')
                              ?.replace(/&lt;/g, '<')
                              ?.replace(/&gt;/g, '>')
                              ?.replace(/&quot;/g, '"')
                              ?.replace(/&#39;/g, "'") ?? ''
                          }
                          onChange={(e) => handleTitleChange(e.target.value)}
                        />
                      ) : (
                        <Typography
                          component='div'
                          variant='h5'
                        >
                          {manualHelper.formatManualNodeTitle(policyQuery.data, manual)}
                        </Typography>
                      )}
                    </Stack>
                    {isOpenModifyPolicy ? (
                      <>
                        {policyData?.sections && (
                          <ModifyPolicyStructure
                            onClose={() => setIsOpenModifyPolicy(false)}
                            sections={policyData.sections}
                            policyData={policyData}
                            manual={manual}
                            updatePolicyData={updatePolicyData}
                            onChange={(updatedSection) => policyData && handleSectionChange(updatedSection)}
                          />
                        )}
                      </>
                    ) : (
                      <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                        <Stack spacing={policyMode === 'EDITING' ? 0 : 10}>
                          {filteredSections()?.map((section, sectionIndex) => {
                            // Always use contentEditable for draft manuals to show tracked changes
                            // content is only used for publishing to issued manuals (no track changes)
                            const sectionContentToRender = section.contentEditable ?? section.content ?? '';
                            const isEditingMode = policyMode === 'EDITING';

                            return (
                              <Box key={`${section.id}_${sectionIndex}`}>
                                {isEditingMode ? (
                                  <>
                                    <Box sx={{ p: 0, minHeight: 0 }}>
                                      <Box sx={{ minHeight: 0 }}>
                                        <Stack spacing={3}>
                                          <SectionEditor
                                            key={`${section.id}_${sectionIndex}`}
                                            section={section}
                                            sectionId={section.id}
                                            sectionNumber={`${manualHelper.getNumberManualNode(policyData, manual)}.${manualHelper.getEnabledSectionIndex(section.id, policyData)}`}
                                            isEditable={
                                              !!(currentSection === section.id) &&
                                              policyMode === 'EDITING' &&
                                              section.state !== 'DISABLED'
                                            }
                                            viewOnly={!!(policyMode !== 'EDITING' || section.state === 'DISABLED')}
                                            isDisabled={section.state === 'DISABLED'}
                                            manual={manual}
                                            issuedAt={policyData?.issuedAt}
                                          />
                                        </Stack>
                                      </Box>
                                    </Box>
                                  </>
                                ) : (
                                  <Box>
                                    <Stack spacing={1}>
                                      <SectionTags
                                        section={section}
                                        agencyModified={section.agencyModified}
                                        policyData={policyData}
                                        manual={manual}
                                      />
                                      <Typography
                                        className={`title_${section.id}`}
                                        component='div'
                                        variant='h6'
                                        sx={{ opacity: section.state === 'DISABLED' ? 0.5 : 1 }}
                                      >
                                        {manualHelper.formatPolicyNodeTitle(section, policyNumber, policyData)}
                                      </Typography>
                                      <Box
                                        sx={{
                                          minHeight: 0,
                                          overflow: 'auto',
                                          opacity: section.state === 'DISABLED' ? 0.5 : 1,
                                        }}
                                      >
                                        {parse(
                                          contentConversionHelper.contentForRender(sectionContentToRender, {
                                            // Preserve insert nodes in contentEditable so tracked changes are visible
                                            preserveInsertNodes: true,
                                          }),
                                          {
                                            replace(node: DOMNode, ind: number) {
                                              return PolicyContentHelper.handleContent(node, ind);
                                            },
                                          }
                                        )}
                                      </Box>
                                      {section.attachments && section.attachments.length > 0 && (
                                        <Box sx={{ mt: 2, mb: 2 }}>
                                          <Stack
                                            direction='row'
                                            spacing={1}
                                          >
                                            {section.attachments
                                              .filter((att: any) => !att.embedded)
                                              .map((att: any) => (
                                                <AttachmentBox
                                                  key={att.id}
                                                  attachmentRef={att}
                                                  manualId={manual.id}
                                                  onRemove={() => { }}
                                                  isEditable={false}
                                                />
                                              ))}
                                          </Stack>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Box>
                                )}
                                <Stack
                                  spacing={policyMode === 'EDITING' ? 0 : 10}
                                  sx={{ mt: 10 }}
                                >
                                  {filterSubsections(section.subSections).map((subSection, subSectionIndex) => {
                                    // Always use contentEditable for draft manuals to show tracked changes
                                    // content is only used for publishing to issued manuals (no track changes)
                                    const subSectionContentToRender =
                                      subSection.contentEditable ?? subSection.content ?? '';
                                    const isSubSectionInEditingMode = isEditingMode;

                                    return (
                                      <Box key={`${subSection.id}_${subSectionIndex}`}>
                                        {isSubSectionInEditingMode ? (
                                          <SectionEditor
                                            key={`${subSection.id}_${subSectionIndex}`}
                                            section={subSection}
                                            sectionId={section.id}
                                            isSubSection
                                            isLastItem={subSectionIndex === section.subSections.length - 1}
                                            sectionNumber={`${manualHelper.getNumberManualNode(policyData, manual)}.${manualHelper.getEnabledSectionIndex(section.id, policyData)}.${manualHelper.getEnabledSubSectionIndex(section.id, subSection.id, policyData)}`}
                                            isEditable={
                                              !!(currentSection === subSection.id) &&
                                              policyMode === 'EDITING' &&
                                              subSection.state !== 'DISABLED'
                                            }
                                            viewOnly={!!(policyMode !== 'EDITING' || subSection.state === 'DISABLED')}
                                            isDisabled={section.state === 'DISABLED' || subSection.state === 'DISABLED'}
                                            manual={manual}
                                            issuedAt={policyData?.issuedAt}
                                          />
                                        ) : (
                                          <Box sx={{ ml: 5 }}>
                                            <Stack spacing={1}>
                                              <SectionTags
                                                section={subSection}
                                                agencyModified={subSection.agencyModified}
                                                policyData={policyData}
                                                manual={manual}
                                              />
                                              <Typography
                                                className={`title_${subSection.id}`}
                                                component='div'
                                                variant='h6'
                                                sx={{ opacity: subSection.state === 'DISABLED' ? 0.5 : 1 }}
                                              >
                                                {manualHelper.formatPolicyNodeTitle(
                                                  subSection,
                                                  policyNumber,
                                                  policyData
                                                )}
                                              </Typography>
                                              <Box
                                                sx={{
                                                  minHeight: 0,
                                                  overflow: 'auto',
                                                  opacity: subSection.state === 'DISABLED' ? 0.5 : 1,
                                                }}
                                              >
                                                {parse(
                                                  contentConversionHelper.contentForRender(subSectionContentToRender, {
                                                    // Preserve insert nodes in contentEditable so tracked changes are visible
                                                    preserveInsertNodes: true,
                                                  }),
                                                  {
                                                    replace(node: DOMNode, ind: number) {
                                                      return PolicyContentHelper.handleContent(node, ind);
                                                    },
                                                  }
                                                )}
                                              </Box>
                                              {subSection.attachments && subSection.attachments.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                  <Stack
                                                    direction='row'
                                                    spacing={1}
                                                  >
                                                    {subSection.attachments
                                                      .filter((att: any) => !att.embedded)
                                                      .map((att: any) => (
                                                        <AttachmentBox
                                                          key={att.id}
                                                          attachmentRef={att}
                                                          manualId={manual.id}
                                                          onRemove={(id) => handleRemoveAttachment(subSection, id)}
                                                          isEditable={false}
                                                        />
                                                      ))}
                                                  </Stack>
                                                </Box>
                                              )}
                                            </Stack>
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Stack>
                                {policyMode === 'EDITING' &&
                                  !disabledFilter &&
                                  areAllSectionsDisabled() &&
                                  sectionIndex === (filteredSections()?.length ?? 0) - 1 &&
                                  renderAddSectionButton(() => handleInsertSectionAfter(section.id))}
                              </Box>
                            );
                          })}

                          {filteredSections()?.length === 0 &&
                            policyMode === 'EDITING' &&
                            !disabledFilter &&
                            renderAddSectionButton(() => handleInsertSectionAfter(''))}
                        </Stack>
                        {parse(contentConversionHelper.contentForRender(policyQuery.data?.content ?? ''), {
                          replace(node: DOMNode, ind: number) {
                            return PolicyContentHelper.handleContent(node, ind);
                          },
                        })}
                      </Box>
                    )}
                  </>
                ) : null}
              </Stack>
            )}
          </ContentPage>
          <PolicyCompare id={id} />
        </Stack>
      </TakeoverLayout>
      <ReleaseNotesModal
        key={`releaseNotes-${currentNote?.updatedAt || policyData?.releaseNotes?.history?.length || 'new'}-${activeModalPane === 'releaseNotes' ? 'open' : 'closed'}`}
        open={activeModalPane === 'releaseNotes'}
        onClose={handleCloseModals}
        initialData={modalAction === 'EDIT' ? currentNote : undefined}
        status={notesStatus}
        onSave={handleUpdateReleaseNotes}
        manualLevel={manual?.manualLevel}
      />
      <GuideSheetEditModal
        key={`guideSheet-${policyData?.guideSheet?.updatedAt || 'new'}-${activeModalPane === 'guideSheet' ? 'open' : 'closed'}`}
        open={activeModalPane === 'guideSheet'}
        onClose={handleCloseModals}
        initialTitle={policyData?.guideSheet?.title}
        initialStatus={policyData?.guideSheet?.draftState as 'DRAFT' | 'APPROVED'}
        initialContent={policyData?.guideSheet?.contentEditable}
        onSave={handleGuideSheetSave}
        editorHeight={400}
        onStatusChange={async (newStatus) => {
          if (newStatus === 'APPROVED' && manual && policyData) {
            await approveGuideSheet(manual.id, policyData.id, () => {
              queryClient.invalidateQueries(['draft_policy', manual.id, policyData.id]);
              queryClient.invalidateQueries(['draft_preface', manual.id, policyData.id]);
            });
          }
        }}
        manualLevel={manual?.manualLevel}
      />
    </>
  );
};

export default PolicyEditor;
